import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import React from "react";
import { UserImportJobFailureCounts } from "../../../../../services/api/types/users/userActions";
import {
  brand,
  error as errorColors,
  gray,
  success as successColors,
  warning as warningColors,
} from "../../../../../theme/themePrimitives";
import { UseBulkUserImportReturn } from "../../useBulkUserImport";

interface ValidationItem {
  title: string;
  description: string;
}

type ValidationOutcomeProps = Pick<
  UseBulkUserImportReturn,
  | "validation"
  | "isValidating"
  | "autoFix"
  | "setAutoFix"
  | "downloadValidationLog"
  | "goToStep"
>;

const FAILURE_LABELS: Record<string, string> = {
  WrongS3Format: "Invalid file format",
  FailedToGetFile: "Could not read uploaded file",
  JobNotFound: "Import job not found",
  InvalidTemplateStructure: "Invalid template structure",
  MissingEmail: "Missing email",
  UnexpectedError: "Unexpected error",
  InvalidDataFormat: "Invalid data format",
  MissingRequiredField: "Missing required fields",
  InvalidAttributeValue: "Invalid attribute value",
};

const toItems = (counts: UserImportJobFailureCounts): ValidationItem[] =>
  Object.entries(counts).map(([type, count]) => ({
    title: FAILURE_LABELS[type] ?? type,
    description: `${count} ${count === 1 ? "occurrence" : "occurrences"} found`,
  }));

export const ValidationOutcome: React.FC<ValidationOutcomeProps> = ({
  validation,
  isValidating,
  autoFix,
  setAutoFix,
  downloadValidationLog,
  goToStep,
}) => {
  const errorItems = validation ? toItems(validation.errors) : [];
  const warningItems = validation ? toItems(validation.warnings) : [];
  const hasErrors = errorItems.length > 0;
  const hasWarnings = warningItems.length > 0;
  const canProceed = !!validation && !hasErrors;

  return (
    <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Validation Outcome</Typography>
        {(hasErrors || hasWarnings) && (
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={downloadValidationLog}
            sx={{
              borderRadius: "12px",
              borderColor: gray[300],
              color: "text.primary",
              fontWeight: 600,
              "&:hover": { borderColor: gray[400], bgcolor: gray[50] },
            }}
          >
            Download validation log
          </Button>
        )}
      </Box>

      {isValidating && !validation ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            py: 6,
          }}
        >
          <CircularProgress sx={{ color: brand[600] }} />
          <Typography variant="body2" color="text.secondary">
            Validating your file…
          </Typography>
        </Box>
      ) : !validation ? null : (
        <>
          {(hasWarnings || hasErrors) && (
            <Stack>
              {hasWarnings && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    border: `1px solid ${gray[200]}`,
                    borderRadius: hasErrors
                      ? "12px 12px 0px 0px !important"
                      : "12px !important",
                    backgroundColor: gray[50],
                    "&:before": { display: "none" },
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: gray[500] }} />}
                    sx={{
                      px: 2,
                      py: 1,
                      "& .MuiAccordionSummary-content": {
                        my: "10px",
                        alignItems: "center",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: warningColors[100],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <InfoOutlinedIcon
                          sx={{ color: warningColors[600], fontSize: 20 }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Warnings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {warningItems.length} attribute values may need review
                          — auto-fix available
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mr: 1,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Auto-fix
                      </Typography>
                      <Switch
                        checked={autoFix}
                        onChange={(e) => setAutoFix(e.target.checked)}
                        color="success"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Stack spacing={1}>
                      {warningItems.map((item, i) => (
                        <Box
                          key={i}
                          sx={{
                            border: `1px solid ${gray[200]}`,
                            borderRadius: "12px",
                            p: 2,
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            backgroundColor: "white",
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: warningColors[500],
                              mt: "5px",
                              flexShrink: 0,
                            }}
                          />
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {item.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.description}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}
              {hasErrors && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    border: `1px solid ${gray[200]}`,
                    borderTop: hasWarnings ? "none" : undefined,
                    borderRadius: hasWarnings
                      ? "0px 0px 12px 12px !important"
                      : "12px !important",
                    backgroundColor: gray[50],
                    "&:before": { display: "none" },
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: gray[500] }} />}
                    sx={{
                      px: 2,
                      py: 1,
                      "& .MuiAccordionSummary-content": { my: "10px" },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flex: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: errorColors[100],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <WarningAmberIcon
                          sx={{ color: errorColors[600], fontSize: 20 }}
                        />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Errors
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {errorItems.length} issues found — must be resolved
                          before proceeding
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                    <Stack spacing={1}>
                      {errorItems.map((item, i) => (
                        <Box
                          key={i}
                          sx={{
                            border: `1px solid ${gray[200]}`,
                            borderRadius: "12px",
                            p: 2,
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.5,
                            backgroundColor: "white",
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: errorColors[500],
                              mt: "5px",
                              flexShrink: 0,
                            }}
                          />
                          <Box>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {item.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.description}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          )}

          {!hasErrors && (
            <Box
              sx={{
                mt: hasWarnings ? 2 : 0,
                bgcolor: successColors[50],
                border: `1px solid ${successColors[200]}`,
                borderRadius: "12px",
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: successColors[600],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <CheckIcon sx={{ color: "#fff", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  color={successColors[700]}
                >
                  File validated successfully
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No errors · {warningItems.length}{" "}
                  {warningItems.length === 1 ? "warning" : "warnings"} · Ready to
                  review changes
                </Typography>
              </Box>
            </Box>
          )}

          {canProceed && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Button
                endIcon={
                  <ArrowForwardIosIcon sx={{ fontSize: "13px !important" }} />
                }
                onClick={() => goToStep(2)}
                sx={{
                  bgcolor: brand[600],
                  "&:hover": { bgcolor: brand[700] },
                  borderRadius: "12px",
                  p: 2,
                  color: "white",
                }}
              >
                Proceed to review
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
